import { ArrowRight as UiArrowRight, Download as UiDownload, Share2 as UiShare2 } from '../components/icons';
// frontend/src/pages/Referral.js
import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../api';
import { buildReferralLink } from '../utils/referral';
import { useNavigate } from 'react-router-dom';

export default function Referral() {
  const [user, setUser] = useState(null);
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({ invited: 0, earned: 0 });
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const qrRef = useRef();
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    let active = true;
    if (!token) {
      navigate('/login_page');
      return;
    }

    const fetchData = async () => {
      setError('');
      try {
        const [profileRes, statsRes] = await Promise.all([
          api.get('/api/users/me', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/api/referral/stats', { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const userData = profileRes.data.user;
        if (!active) return;
        const link = buildReferralLink(window.location.origin, userData?.referral_code);
        if (!link) throw new Error('missing-referral-code');
        setUser(userData);
        setReferralLink(link);
        setStats(statsRes.data);
      } catch (err) {
        if (active) setError('دریافت اطلاعات دعوت ناموفق بود. لطفاً دوباره تلاش کنید.');
      }
    };

    fetchData();
    return () => { active = false; };
  }, [token, navigate, retry]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('کپی لینک انجام نشد؛ می‌توانید لینک زیر را انتخاب و کپی کنید.');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'دعوت به طلافروشی آنلاین',
          text: 'با کد معرف من در ESF Gold ثبت‌نام کنید.',
          url: referralLink,
        });
      } catch (err) {
        if (err.name !== 'AbortError') await handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `دعوت-${user.referral_code}.png`;
    a.click();
  };

  if (error && !user) return <div className="p-8 text-center" role="alert"><p>{error}</p><button onClick={() => setRetry(value => value + 1)} className="mt-4 text-indigo-600">تلاش مجدد</button></div>;
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-purple-600"></div>
          <p className="text-gray-600 font-medium">در حال بارگذاری...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
        <div className="max-w-md mx-auto">
          {error && <p role="alert" className="text-red-600 text-sm mb-4">{error}</p>}

          {/* دکمه برگشت */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ x: -5 }}
            onClick={() => navigate(-1)}
            className="flex items-center text-indigo-600 font-medium mb-6"
          >
            <UiArrowRight className="w-5 h-5 ml-1" />
            برگشت
          </motion.button>

          {/* عنوان */}
          <motion.h1 data-page-title="true"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-10"
          >
            دعوت از دوستان
          </motion.h1>

          {/* QR Code */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            ref={qrRef}
            className="bg-white rounded-3xl p-8 shadow-xl border border-gray-200 mb-8 text-center"
          >
            <div className="relative inline-block">
              <QRCodeCanvas
                value={referralLink}
                size={180}
                level="H"
                includeMargin
                className="rounded-2xl"
              />
            </div>
            <p className="text-sm text-gray-600 mt-4 font-medium">اسکن کنید و دعوت شوید</p>
          </motion.div>

          {/* کد و لینک */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-gray-200 mb-6"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-gray-600 font-medium">کد دعوت شما:</span>
              <span dir="ltr" className="text-xl font-bold text-purple-600 font-mono">{user.referral_code}</span>
            </div>
            <div dir="ltr" className="text-xs text-gray-500 break-all bg-gray-50 p-3 rounded-xl font-mono">
              {referralLink}
            </div>
            <AnimatePresence>
              {copied && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="text-green-600 text-sm font-medium mt-2 text-center"
                >
                  لینک کپی شد!
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* آمار */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl p-5 text-center shadow-lg"
            >
              <div className="text-3xl font-bold">{stats.invited}</div>
              <div className="text-sm font-medium mt-1">دعوت موفق</div>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-2xl p-5 text-center shadow-lg"
            >
              <div className="text-3xl font-bold">{parseFloat(stats.earned).toFixed(3)}</div>
              <div className="text-sm font-medium mt-1">گرم طلا هدیه</div>
            </motion.div>
          </motion.div>

          {/* دکمه‌ها */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="space-y-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleShare}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-3"
            >
              <UiShare2 className="w-6 h-6" />
              اشتراک‌گذاری لینک
            </motion.button>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={downloadQR}
              className="w-full bg-gradient-to-r from-gray-700 to-gray-900 text-white py-4 rounded-2xl font-bold shadow-xl hover:shadow-2xl transition-all flex items-center justify-center gap-3"
            >
              <UiDownload className="w-6 h-6" />
              دانلود QR کد
            </motion.button>
          </motion.div>

          {/* توضیحات */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-10 text-center text-xs text-gray-600 bg-white/70 backdrop-blur-sm rounded-2xl p-4 border border-gray-200"
          >
            <p className="mb-1">به ازای هر دوست که با کد شما ثبت‌نام کند و اولین خرید/فروش را انجام دهد،</p>
            <p className="font-bold text-purple-600 text-lg">0.005 گرم طلا هدیه بگیرید!</p>
          </motion.div>
        </div>
      </div>
    </>
  );
}
